import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { 
  Search, 
  Plus, 
  Menu, 
  LogOut, 
  ChevronDown,
  Sparkles,
  PhoneCall,
  Database
} from 'lucide-react';

interface CrmHeaderProps {
  onToggleSidebar?: () => void;
  isSidebarCollapsed?: boolean;
}

export function CrmHeader({ onToggleSidebar }: CrmHeaderProps) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  return (
    <header className="sticky top-0 z-40 bg-slate-900 border-b border-slate-800 shadow-md">
      <div className="px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        {/* Left: Brand Logo & Toggle */}
        <div className="flex items-center space-x-4">
          <button
            type="button"
            onClick={onToggleSidebar}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
            title="Toggle Sidebar"
          >
            <Menu className="w-5 h-5" />
          </button>

          <Link to="/dashboard" className="flex items-center space-x-3 group">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-indigo-600 via-indigo-500 to-purple-500 flex items-center justify-center text-white shadow-lg shadow-indigo-600/30 font-extrabold text-base tracking-wider font-heading group-hover:scale-105 transition-transform">
              CB
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-extrabold text-white text-base tracking-tight font-heading">
                  CloudBinary <span className="text-indigo-400">CRM</span>
                </span>
                <span className="hidden md:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-950/90 text-emerald-400 border border-emerald-800/80">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping mr-1"></span>
                  AWS Live
                </span>
              </div>
              <p className="text-[10px] text-slate-400 hidden sm:block">
                Inbound Sales, Admissions & Inquiries Management Suite
              </p>
            </div>
          </Link>
        </div>

        {/* Center: Global Quick Search */}
        <div className="hidden lg:flex items-center space-x-3 flex-1 max-w-md mx-8">
          <div className="relative w-full">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              placeholder="Search leads by name, phone (+91), email, or course..."
              onClick={() => navigate('/leads')}
              className="w-full pl-10 pr-4 py-2 bg-slate-950/80 border border-slate-800 rounded-xl text-xs text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all cursor-pointer"
              readOnly
            />
          </div>
        </div>

        {/* Right: Quick Actions & Profile */}
        <div className="flex items-center space-x-3">
          <a
            href="https://wa.me/919100073006"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-950/70 border border-emerald-800/60 text-emerald-400 hover:bg-emerald-900/50 text-xs font-semibold transition-all shadow-xs"
            title="Open WhatsApp Hotline"
          >
            <PhoneCall className="w-3.5 h-3.5 text-emerald-400" />
            <span className="font-mono">+91 9100073006</span>
          </a>

          <Link
            to="/leads?action=create"
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold shadow-md shadow-indigo-600/30 transition-all hover:scale-[1.02] active:scale-95"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden md:inline">New Lead</span>
          </Link>

          {/* User Profile Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className="flex items-center space-x-2 p-1.5 bg-slate-800 hover:bg-slate-700 rounded-xl text-slate-200 transition-all border border-slate-700/60"
            >
              <div className="w-7 h-7 rounded-lg bg-gradient-to-tr from-indigo-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold">
                {user?.name ? user.name.charAt(0).toUpperCase() : 'A'}
              </div>
              <span className="text-xs font-medium max-w-[100px] truncate hidden md:block">
                {user?.name || user?.email?.split('@')[0] || 'Admin'}
              </span>
              <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
            </button>

            {showProfileMenu && (
              <div 
                className="absolute right-0 mt-2 w-56 bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl py-2 z-50 text-xs text-slate-200 animate-in fade-in slide-in-from-top-2 duration-150"
                onClick={() => setShowProfileMenu(false)}
              >
                <div className="px-4 py-2 border-b border-slate-800">
                  <p className="font-semibold text-white truncate">{user?.name || 'Admissions Counselor'}</p>
                  <p className="text-[11px] text-slate-400 truncate">{user?.email || 'sales@c3ops.in'}</p>
                  <span className="inline-block mt-1 px-2 py-0.5 text-[10px] font-bold rounded bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 uppercase tracking-wider">
                    {user?.role || 'Admin'}
                  </span>
                </div>

                <Link
                  to="/dashboard"
                  className="flex items-center px-4 py-2.5 text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
                >
                  <Sparkles className="w-4 h-4 mr-2.5 text-indigo-400" />
                  CRM Dashboard
                </Link>

                <Link
                  to="/campaign-tools"
                  className="flex items-center px-4 py-2.5 text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
                >
                  <Database className="w-4 h-4 mr-2.5 text-emerald-400" />
                  WhatsApp Webhook Setup
                </Link>

                <div className="border-t border-slate-800 my-1"></div>

                <button
                  onClick={handleSignOut}
                  className="w-full flex items-center px-4 py-2.5 text-rose-400 hover:bg-rose-950/40 hover:text-rose-300 transition-colors"
                >
                  <LogOut className="w-4 h-4 mr-2.5" />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
